import { useTranslation } from "react-i18next";

import Modal, { ModalButton, ModalSecondaryButton } from "../../../../../../components/Modal";
import { GlobeIcon } from "../../../../../../icons/Globe";
import { FreeDomainCta, FreeDomainModalContent } from "../../styles";

// What comes with a domain bought at Keepo (the domain search page's benefits)
const BENEFITS = [
  { key: "privacy", icon: "🛡️" },
  { key: "email", icon: "✉️" },
  { key: "keepo", icon: "⚡" },
  { key: "ssl", icon: "🔒" },
];

type Props = {
  domain: string;
  /** A year's price, already formatted (e.g. "R$ 99,90"). */
  price: string;
  onRegister: () => void;
  onClose: () => void;
};

/**
 * The domain typed for another provider is free (nobody registered it): said in a modal that stands out (the Plus
 * tone), with its price, what comes with it and Register (the checkout, connecting it to this Keepo). Closed, the
 * offer stays in the form.
 */
export const FreeDomainModal = ({ domain, price, onRegister, onClose }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.settings.customDomain.keepoDomain.offerModal";

  return (
    <Modal
      tone="plus"
      outsideExit
      labelledBy="free-domain-title"
      onExit={onClose}
      exitLabel={t(`${prefix}.close`)}
      footer={
        <>
          <ModalSecondaryButton type="button" onClick={onClose}>
            {t(`${prefix}.later`)}
          </ModalSecondaryButton>
          <FreeDomainCta>
            <span className="fire-halo" />
            <span className="fire-ring" />
            <ModalButton className="cta" onClick={onRegister}>
              {t(`${prefix}.register`)}
            </ModalButton>
          </FreeDomainCta>
        </>
      }>
      <FreeDomainModalContent>
        <div className="hero">
          <span className="globe" aria-hidden="true">
            <GlobeIcon size={32} />
          </span>
          <span className="tag">{t(`${prefix}.tag`)}</span>
          <h2 id="free-domain-title">
            <span className="domain">{domain}</span> {t(`${prefix}.title`)}
          </h2>
          <p>{t("pages.settings.customDomain.keepoDomain.offer.text")}</p>
          <p className="price">
            <b>{price}</b>
            <span>{t(`${prefix}.perYear`)}</span>
          </p>
        </div>

        <ul className="benefits">
          {BENEFITS.map(({ key, icon }) => (
            <li key={key}>
              <span aria-hidden="true">{icon}</span>
              {t(`pages.domains.benefits.${key}.title`)}
            </li>
          ))}
        </ul>
      </FreeDomainModalContent>
    </Modal>
  );
};
